import { useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { ActivityTimeline } from '@/app/components/activity-timeline';
import { usePortalProjectActivity, usePortalProjects } from '@/hooks/use-projects';
import type { ProjectActivityEvent } from '@/lib/types';

export function PortalProjectActivityPage() {
  const { token, projectId } = useParams<{ token: string; projectId: string }>();
  const pid = parseInt(projectId ?? '0');
  const [page, setPage] = useState(1);
  const [allEvents, setAllEvents] = useState<ProjectActivityEvent[]>([]);

  const { data: projects } = usePortalProjects(token ?? '');
  const project = projects?.find((p) => p.id === pid);

  const { data, isLoading, isFetching } = usePortalProjectActivity(
    token ?? '',
    pid,
    page,
  );

  // Accumulate events as pages load
  const events =
    page === 1
      ? data?.results ?? []
      : [...allEvents, ...(data?.results ?? [])];

  // Deduplicate by building a stable list
  const seen = new Set<string>();
  const dedupedEvents = events.filter((e) => {
    const key = `${e.type}-${e.timestamp}-${e.headline}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const hasMore = (data?.page ?? 1) < (data?.total_pages ?? 1);

  const allEventsRef = useRef(dedupedEvents);
  // eslint-disable-next-line react-hooks/refs
  allEventsRef.current = dedupedEvents;

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setAllEvents(allEventsRef.current);
      setPage((p) => p + 1);
    }
  }, [hasMore, isFetching]);

  if (isLoading && page === 1) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <>
      <nav className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/portal/${token}`}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Projects
            </Link>
          </Button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              {project ? `Activity — ${project.name}` : 'Project Activity'}
            </CardTitle>
            {data && (
              <p className="text-sm text-gray-500">
                {data.total_count} event{data.total_count !== 1 ? 's' : ''}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <ActivityTimeline
              events={dedupedEvents}
              hasMore={hasMore}
              isLoadingMore={isFetching && page > 1}
              onLoadMore={handleLoadMore}
            />
          </CardContent>
        </Card>
      </main>
    </>
  );
}
